import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";
import { Box, Checkbox, Chip, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from "@mui/material";
import { AgGridReact } from "ag-grid-react";

import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import { DPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";

import { FilterButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import bfAlgoRuleService from "@plannel/services/dp/bf-algo-rule-service";
import planConfigService from "@plannel/services/data/plan-config-service";

const { snackBarMessageColor } = ColorUtil;

const PAGE_SIZES = [100, 200, 500];

/**
 * 공통 트리 필터를 건드리지 않고 이 화면에서만 다중 선택을 칩(첫 항목 + +N)으로 축약 표시하는
 * 오버레이. 포커스가 들어오면(클릭·타이핑) 오버레이를 걷어 원래 입력·검색 UI 를 그대로 노출한다.
 * 칩 영역은 pointerEvents 없음 — 클릭이 아래 입력으로 그대로 전달된다.
 */
const CondensedFilterOverlay = ({ nodes, children }) => {
  const [focused, setFocused] = useState(false);
  const names = (nodes ?? []).map((n) => n?.name).filter(Boolean);
  const show = !focused && names.length > 1;
  return (
    <Box
      sx={{ position: "relative", display: "inline-flex" }}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={() => setFocused(false)}
    >
      {children}
      {show && (
        <Box
          sx={{
            position: "absolute", top: "50%", transform: "translateY(-50%)",
            left: 12, right: 34, display: "flex", alignItems: "center", gap: 0.5,
            bgcolor: "background.paper", pointerEvents: "none", overflow: "hidden",
          }}
        >
          <Chip size="small" label={names[0]} sx={{ minWidth: 0, flexShrink: 1 }} />
          <Chip size="small" label={`+${names.length - 1}`} sx={{ flexShrink: 0 }} />
        </Box>
      )}
    </Box>
  );
};

/**
 * 수요예측 알고리즘 규칙 (z_bf_algo_rule) — 수요계획 > 설정.
 *
 * 행 = 거래처 x 품목 조합. 저장은 범위 룰이지만 이 화면은 룰을 현재 마스터로 전개해 보여준다
 * ("조합 전개" 체크 해제 시에는 전개 없이 룰 1건 = 1행 — CSV 로 올린 내용과 열이 1:1 로 맞는다)
 * (조회 시 계산, 저장 안 함). 같은 조합에 룰이 여럿 걸리면 엔진과 같은 우선순위로 이기는 룰 하나만
 * 보이므로 "이 조합엔 무슨 알고리즘이 적용되나"를 그대로 읽을 수 있다. 신제품·거래처가 추가되면
 * 다시 조회만 해도 조합이 늘어난다.
 * 서버 페이징 그리드로 계층 트리·코드/명 검색·적용 알고리즘 필터·상세 필터(그리드 컬럼 조건, 다른 마스터 화면과 동일),
 * 엑셀 내보내기(조합 단위),
 * 알고리즘 인라인 수정(그 조합의 룰 전체에 적용)·룰 삭제(선택 조합의 룰)를 제공한다.
 * 코드별 규칙 CSV 업로드는 데이터 통합 > 데이터 관리 > CSV 가져오기(bfAlgoRule)에서 한다.
 * BF Settings 카드의 "세부사항"은 viewState.ruleIds 로 그 카드 줄의 룰들만 걸어 이 화면을 연다(칩 X 로 해제).
 */
const BfAlgoRuleMgmt = ({ viewName, title }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName) ?? {};
  const { bfAlgorithm } = codeUtil;

  const gridRef = useRef();
  const [rows, setRows] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);

  // 계층 트리 필터(customerHrchys/category — 컴포넌트가 viewState.filter 에 자체 영속) + 코드/명 검색
  const [filterValue, setFilterValue] = useState(reduxViewState.filter ?? {});
  const customerSearch = useRef(reduxViewState.customerSearch ?? "");
  const itemSearch = useRef(reduxViewState.itemSearch ?? "");
  // 적용 알고리즘: 멀티선택(선택한 알고리즘을 모두 포함하는 조합)
  const [models, setModels] = useState(reduxViewState.models ?? []);
  // 신제품 필터: "ALL" 전체 / "Y" 신규 상품만 / "N" 기존 품목만 (실품목 조합에만 적용).
  // 빈 문자열을 값으로 쓰면 MUI 라벨이 떠오르지 않아 글자가 겹친다 — 전체도 실제 값으로 둔다.
  const [npiFilter, setNpiFilter] = useState(reduxViewState.npiFilter ?? "ALL");
  // BF Settings 세부사항 진입 — 그 카드 줄의 룰 id 들(문자열, jsonbig 안전)과 표시 라벨
  const [ruleIds, setRuleIds] = useState(reduxViewState.ruleIds ?? []);
  const [ruleLabel, setRuleLabel] = useState(reduxViewState.ruleLabel ?? "");
  // 상세 필터(그리드 컬럼 조건 트리) — 서버에서 rows CTE 위의 WHERE 로 적용(DpColumn.BfAlgoRule)
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState.advancedFilters ?? null);
  // 조합 전개 — 기본 켜짐(적용 대상 확인용). 끄면 저장된 룰 그대로 1건 = 1행 (CSV 가져온 내용 확인용)
  const [expand, setExpand] = useState(reduxViewState.expand ?? true);
  const [openFilter, setOpenFilter] = useState(false);
  const hasAdvancedFilter = !isEmpty(advancedFilters) && (advancedFilters?.children?.length ?? 0) > 0;

  // 마운트 시점 선택값 — 트리 필터의 treeload 이펙트가 defaultValue 로 표시·선택 상태를 초기화한다
  const initialCustSel = useRef(reduxViewState.filter?.customerHrchys ?? []);
  const initialItemSel = useRef(reduxViewState.filter?.category ?? []);

  const [currentPage, setCurrentPage] = useState(reduxViewState.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState.pageSize ?? PAGE_SIZES[0]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  // 적용 룰을 짧은 글로 — 어느 룰에서 온 알고리즘인지 볼 때만 필요해 기본은 숨기고, 세부사항 진입(ruleIds)
  // 때만 자동으로 켠다: 카드의 룰 대신 다른 룰이 적용된 조합(예: 신제품 룰이 이긴 행)의 이유가 바로 읽힌다.
  // 계층 노드는 "해외 하위 전체" / "해외 이 계층만", 실체는 코드/명 그대로, 신제품 룰은 "신규 상품: true".
  const axisText = (name, hrchyFlg, leafScope) =>
    hrchyFlg === "Y" ? `${name} ${leafScope === "Y" ? t("scopeLeafAll") : t("scopeNodeOnly")}` : name;
  const ruleTextOf = (r) => {
    if (!r?.appliedRuleId) return null;
    const cust = axisText(r.ruleCustName ?? r.ruleCustCd ?? "-", r.ruleCustHrchyFlg, r.ruleCustLeafScope);
    const item = r.ruleNpiFlg != null
      ? `${t("newProduct")}: ${String(r.ruleNpiFlg === "Y")}`
      : axisText(r.ruleItemName ?? r.ruleItemCd ?? "-", r.ruleItemHrchyFlg, r.ruleItemLeafScope);
    return `${cust} x ${item}`;
  };

  // BF Settings 활성 모델이 마스터 — 필터·편집 드랍다운 모두 활성 모델만 제공한다.
  // (엔진도 동일 원칙으로 비활성 모델을 실행하지 않는다 — forecast.py RULE_FILTER)
  // BF_PARAMETER.algorithm 키 = 활성 셋(+default 는 체크 무관 항상 포함). 로드 실패 시 전체 폴백.
  const [activeModels, setActiveModels] = useState(null);
  useEffect(() => {
    planConfigService.getAll({ moduleCd: codeUtil.module.DP, activeFlg: true })
      .then((response) => {
        const bfParam = (response.data?.results ?? []).find((r) => r.configCd === codeUtil.dpConfig.BF_PARAMETER);
        const keys = Object.keys(bfParam?.objVal?.algorithm ?? {});
        setActiveModels(keys.length ? keys : null);
      })
      .catch((e) => debug(e));
  }, []);

  const modelOptions = useMemo(() => {
    const all = Object.values(bfAlgorithm).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
    return activeModels ? all.filter((a) => activeModels.includes(a.cd)) : all;
  }, [bfAlgorithm, activeModels]);

  const getFilterParams = () => ({
    ruleIds: ruleIds.length ? ruleIds : null,
    custHrchyIds: filterValue?.customerHrchys?.map((h) => h.id).filter(Boolean) ?? null,
    itemHrchyIds: filterValue?.category?.map((h) => h.id).filter(Boolean) ?? null,
    customerCd: customerSearch.current || null,
    itemCd: itemSearch.current || null,
    npiFlg: npiFilter === "ALL" ? null : npiFilter,
    models: models.length ? models : null,
    advancedFilters: hasAdvancedFilter ? advancedFilters : null,
    expand,
  });

  const retrieveRules = () => {
    bfAlgoRuleService.page({
      page: currentPage - 1,
      pageSize,
      ...getFilterParams(),
    })
      .then((response) => {
        const { results, totalPages: pages, totalItems: total } = response.data ?? {};
        if (!results?.length) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setRows(results ?? []);
        setTotalPages(pages ?? 0);
        setTotalItems(total ?? 0);
        // 영속된 currentPage 가 필터된 결과의 페이지 수를 넘으면(세부사항 진입 등) 1페이지로 복구
        if ((pages ?? 0) > 0 && currentPage > (pages ?? 0)) {
          setCurrentPage(1);
        }
      })
      .catch((e) => debug(e))
      .then(() => {
        reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(viewName, {
          currentPage, pageSize, models, npiFilter, ruleIds, ruleLabel, advancedFilters, expand,
          customerSearch: customerSearch.current, itemSearch: itemSearch.current,
        })));
        DataState.initialize(gridRef.current?.api);
      });
  };

  useEffect(() => {
    if (gridLoading) {
      retrieveRules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, currentPage, pageSize]);

  // 필터(계층·알고리즘·룰) 변경은 1페이지부터 다시 조회. 검색(autocomplete)은 retrieve 콜백으로 진입
  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveRules();
    }
  };

  useEffect(() => {
    if (!gridLoading) return;
    initPageToRetrieve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, models, npiFilter, ruleIds, advancedFilters, expand]);

  const clearRuleFilter = () => {
    setRuleIds([]);
    setRuleLabel("");
  };

  // 레벨 열이 빈 행이 최하단 레벨인지. 실거래처·실품목이거나(전개 ON 의 리프 행),
  // 계층 노드이면서 범위가 "하위 전체"인 경우(전개 OFF 의 범위 룰 행)가 최하단이다.
  // 노드 자체 룰은 서버가 그 노드의 레벨명을 채워 주므로 여기까지 오지 않는다.
  const isCustLeafLevel = (d) => d?.customerHrchyFlg === "N" || d?.ruleCustLeafScope === "Y";
  const isItemLeafLevel = (d) => d?.itemHrchyFlg === "N" || d?.ruleItemLeafScope === "Y";

  // headerName 은 l10n 키 — GridUtils.getColumnDefs 가 번역한다.
  // 인라인 수정은 알고리즘(models)만 — 조합이 아니라 그 조합의 룰(appliedRuleId)에 저장된다.
  const columnDefs = useMemo(() => [
    // 레벨 = 이 조합의 축이 어느 레벨인지(리프면 Customer/Item, 노드면 그 레벨명), 상위 계층 = 바로 위 노드명.
    // 리프 조합과 노드 조합이 한 그리드에 섞이므로 두 정보를 열로 나눠 표기를 통일한다.
    // filterName 은 상세 필터의 바인딩 키다. headerName(=l10n 키, 표시 라벨)을 그대로 쓰면
    // AdvancedFilterUtil 의 전역 customColumnHandlers 가 같은 이름("customerNodeCd"/"itemNodeCd")을 가로채
    // 재무계획·판매계획용 SQL(customer.customer_cd ...)을 만들어 이 화면 조회가 깨진다. 키만 따로 둔다.
    { headerName: "customerNodeCd", filterName: "bfRuleCustomerNodeCd", field: "customerCd", type: "nonEditableColumn", filterType: "string", width: 150 },
    { headerName: "customerNodeName", field: "customerName", type: "nonEditableColumn", filterType: "string", width: 180 },
    { headerName: "custLevel", field: "customerLevel", type: "nonEditableColumn", filterType: "string", width: 110,
      valueGetter: (p) => p.data?.customerLevel ?? (isCustLeafLevel(p.data) ? codeUtil.customer.name : null) },
    { headerName: "customerHrchy", field: "customerHrchy", type: "nonEditableColumn", filterType: "string", width: 150 },
    { headerName: "itemNodeCd", filterName: "bfRuleItemNodeCd", field: "itemCd", type: "nonEditableColumn", filterType: "string", width: 160 },
    { headerName: "itemNodeName", field: "itemName", type: "nonEditableColumn", filterType: "string", width: 200 },
    { headerName: "itemLevel", field: "itemLevel", type: "nonEditableColumn", filterType: "string", width: 110,
      valueGetter: (p) => p.data?.itemLevel ?? (isItemLeafLevel(p.data) ? codeUtil.item.name : null) },
    { headerName: "itemHrchy", field: "itemHrchy", type: "nonEditableColumn", filterType: "string", width: 150 },
    { headerName: "npi", field: "npiFlg", type: "nonEditableColumn", filterType: "string", width: 90 },
    // 알고리즘: 고정 종류 멀티선택(agRichSelectCellEditor) — 자유 입력 오타 차단.
    // 셀 값은 배열(valueGetter), 저장 데이터(data.models)는 ';' 문자열 유지
    { headerName: "algorithm", field: "models", filterType: "string", width: 240,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      cellEditorParams: () => ({
        values: modelOptions.map((a) => a.cd),
        multiSelect: true,
        suppressDeselectAll: true,
      }),
      valueGetter: (p) => (p.data?.models ?? "").split(";").filter(Boolean),
      valueSetter: (p) => {
        const next = (Array.isArray(p.newValue) ? p.newValue : [p.newValue])
          .filter(Boolean).join(";");
        if (!next || next === (p.data.models ?? "")) return false;   // 전체 해제는 무시 (빈 룰 방지)
        p.data.models = next;
        return true;
      },
      valueFormatter: (p) => (Array.isArray(p.value) ? p.value.join(";") : (p.value ?? "")) },
    { headerName: "appliedRule", field: "appliedRuleId", type: "nonEditableColumn", filterType: "string", width: 260,
      valueGetter: (p) => ruleTextOf(p.data), hide: !ruleIds.length },
    // 생성·수정 정보는 그 조합에 적용된 룰(z_bf_algo_rule) 기준
    { headerName: "createdTs", field: "ruleCreatedTs", type: "nonEditableColumn", filterType: "timestamp",
      valueFormatter: dateUtils.formatDateTime },
    { headerName: "createdBy", field: "ruleCreatedUser", colId: "createdBy", type: "nonEditableColumn",
      filterType: "string", width: 150 },
    { headerName: "updatedTs", field: "ruleUpdatedTs", type: "nonEditableColumn", filterType: "timestamp",
      valueFormatter: dateUtils.formatDateTime },
    { headerName: "updatedBy", field: "ruleUpdatedUser", colId: "updatedBy", type: "nonEditableColumn",
      filterType: "string", width: 150 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [modelOptions, ruleIds.length]);

  // 상세 필터 컬럼: 그리드 컬럼 중 서버가 바인딩하는 것만(적용 룰 텍스트는 화면 파생이라 제외).
  // headerName 이 바인딩 키(DpColumn.BfAlgoRule)이고 패널이 같은 키로 번역한다. 신제품은 Y/N 선택.
  const filterColumnDefs = useMemo(() => columnDefs
    .filter((c) => c.headerName !== "appliedRule")
    .map((c) => (c.headerName === "npi" ? { ...c, filterOption: ["Y", "N"] } : c)),
  [columnDefs]);

  // 선택 조합 → 룰 id 집합 (조합 여러 개가 같은 룰일 수 있다)
  const selectedRuleIds = () => {
    const ids = (gridRef.current?.api?.getSelectedRows() ?? []).map((r) => r.appliedRuleId).filter((id) => id != null).map(String);
    return [...new Set(ids)];
  };

  const removeSelected = () => {
    const ids = selectedRuleIds();
    if (!ids.length) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }
    bfAlgoRuleService.remove(ids)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
      .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
      .then(() => retrieveRules());
  };

  // 변경된 조합의 알고리즘 → 룰 단위로 모아 저장 (같은 룰을 여러 조합에서 바꿨으면 마지막 값)
  const changedRules = () => {
    const byRule = new Map();
    for (const r of DataState.getAllStateData(gridRef.current?.api).filter((r) => r.appliedRuleId != null)) {
      byRule.set(String(r.appliedRuleId), { id: r.appliedRuleId, models: r.models });
    }
    return [...byRule.values()];
  };

  const saveModels = () => {
    const changed = changedRules();
    if (!changed.length) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }
    bfAlgoRuleService.updateModels(changed)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch((e) => {
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
      })
      .then(() => retrieveRules());
  };

  // 엑셀 내보내기 — 조합·알고리즘만. 그리드 정의는 편집기·배열 valueGetter 가 섞여 있어
  // 내보내기용 평면 정의를 따로 둔다 (models 는 ';' 문자열 그대로, 레벨은 미리 계산한 필드).
  const excelColumnDefs = useMemo(() => [
    { headerName: "customerNodeCd", field: "customerCd", width: 150 },
    { headerName: "customerNodeName", field: "customerName", width: 180 },
    { headerName: "custLevel", field: "custLevel", width: 110 },
    { headerName: "customerHrchy", field: "customerHrchy", width: 150 },
    { headerName: "itemNodeCd", field: "itemCd", width: 160 },
    { headerName: "itemNodeName", field: "itemName", width: 200 },
    { headerName: "itemLevel", field: "itemLevelName", width: 110 },
    { headerName: "itemHrchy", field: "itemHrchy", width: 150 },
    { headerName: "npi", field: "npiFlg", width: 90 },
    { headerName: "algorithm", field: "models", width: 240 },
    { headerName: "createdTs", field: "ruleCreatedTs", valueFormatter: dateUtils.formatDateTime },
    { headerName: "createdBy", field: "ruleCreatedUser", width: 150 },
    { headerName: "updatedTs", field: "ruleUpdatedTs", valueFormatter: dateUtils.formatDateTime },
    { headerName: "updatedBy", field: "ruleUpdatedUser", width: 150 },
  ], []);

  const toExcelRows = (results) => (results ?? []).map((r) => ({
    ...r,
    custLevel: r.customerLevel ?? (isCustLeafLevel(r) ? codeUtil.customer.name : null),
    itemLevelName: r.itemLevel ?? (isItemLeafLevel(r) ? codeUtil.item.name : null),
  }));

  const handleDialog = (action) => {
    setDialogInfo({ open: false, title: "", content: "" });
    if (action === "DELETE") removeSelected();
    else if (action === "SAVE") saveModels();
  };

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  return (
    <>
      <FilterContainer>
        <CondensedFilterOverlay nodes={filterValue?.customerHrchys}>
          <CustomerHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={initialCustSel.current}
            enableSelectParent
            enableMultiSelect
            treeload
            styles={{ width: 230 }}
          />
        </CondensedFilterOverlay>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} sx={{ mr: 1.5 }} />
        <CondensedFilterOverlay nodes={filterValue?.category}>
          <ItemHrchyTreeFilter
            viewName={viewName}
            onChangeFilter={setFilterValue}
            defaultValue={initialItemSel.current}
            enableSelectParent
            enableMultiSelect
            treeload
            styles={{ width: 230 }}
          />
        </CondensedFilterOverlay>
        <DPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} />
        <FormControl size="small" sx={{ width: 200, ml: 1 }}>
          <InputLabel>{t("algorithm")}</InputLabel>
          <Select
            multiple
            value={models}
            label={t("algorithm")}
            onChange={(e) => setModels(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", gap: 0.5, overflow: "hidden", alignItems: "center" }}>
                <Chip size="small" label={bfAlgorithm[selected[0]]?.name ?? selected[0]} sx={{ maxWidth: 120 }} />
                {selected.length > 1 && <Chip size="small" label={`+${selected.length - 1}`} />}
              </Box>
            )}
          >
            {modelOptions.map((a) => (
              <MenuItem key={a.cd} value={a.cd}>
                <Checkbox size="small" checked={models.includes(a.cd)} sx={{ p: 0.5, mr: 0.5 }} />
                {a.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ width: 140, ml: 1 }}>
          <InputLabel>{t("newProduct")}</InputLabel>
          <Select value={npiFilter} label={t("newProduct")} onChange={(e) => setNpiFilter(e.target.value)}>
            <MenuItem value="ALL">{t("all")}</MenuItem>
            <MenuItem value="Y">true</MenuItem>
            <MenuItem value="N">false</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ ml: 1 }}
          control={<Checkbox color="primary" name="expandCombination" checked={expand} onChange={(e) => setExpand(e.target.checked)} />}
          label={t("expandCombination")}
        />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          {/* 세부사항 진입 표시 — 필터 줄 오른쪽 끝에 두어 기본 필터 위치가 밀리지 않게 한다 */}
          {ruleIds.length > 0 && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${t("appliedRule")}: ${ruleLabel || ruleIds.length}`}
              onDelete={clearRuleFilter}
              sx={{ mr: 1.5, maxWidth: 360 }}
            />
          )}
          <ExcelExportButton
            title={title}
            service={bfAlgoRuleService}
            columnDefs={excelColumnDefs}
            setExcelData={toExcelRows}
            totalRows={totalItems}
            params={getFilterParams()}
          />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_RuleDeleteAffects", { count: selectedRuleIds().length }) })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_RuleModelsApplyToRule", { count: changedRules().length }) })} />
          <FilterButton
            label="advancedFilter"
            advancedFilterColor={hasAdvancedFilter ? "info" : "default"}
            onClick={() => setOpenFilter((prev) => !prev)}
          />
        </Box>
      </FilterContainer>
      {/* 그리드 + 상세 필터 패널(오른쪽, 크기 조절) — 다른 마스터 화면과 같은 배치 */}
      <Box className="ag-theme-balham grid-area" mb={1} sx={{ display: "flex", flexDirection: "row" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rows}
            {...DefaultGridSetting({ title, viewName })}
            rowSelection={{
              ...DefaultGridSetting({ title, viewName }).rowSelection,
              checkboxes: true,
              headerCheckbox: true,
            }}
            columnDefs={GridUtils.getColumnDefs(columnDefs)}
            onGridReady={onGridReady}
          />
        </Box>
        {openFilter && (
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={filterColumnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>
      <PaginationContainer
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        pageSizes={PAGE_SIZES}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BfAlgoRuleMgmt);
